import type { LabConfig, TestResult } from '../types';
import { loadWasmCpu } from '../wasm';
import { computeStats, jitterProfile, now, sleep } from '../utils';

const GROUP = 'F_timers';

async function measurePerfNowJitter(samples: number, busyLoopIterations: number, wasm: Awaited<ReturnType<typeof loadWasmCpu>> | null): Promise<number[]> {
  const results: number[] = [];
  let prev = now();
  for (let i = 0; i < samples; i++) {
    if (wasm) {
      wasm.busyLoop(busyLoopIterations);
    } else {
      // Fallback: should not happen in production because WASM is required.
      let x = i;
      for (let j = 0; j < 5000; j++) {
        x = (x * 1664525 + 1013904223) >>> 0;
      }
    }
    const t = now();
    results.push(t - prev);
    prev = t;
  }
  return results;
}

async function measureSetTimeoutJitter(samples: number): Promise<number[]> {
  const results: number[] = [];
  let prev = now();
  for (let i = 0; i < samples; i++) {
    await sleep(0);
    const t = now();
    results.push(t - prev);
    prev = t;
  }
  return results;
}

function measureRafStability(samples: number): Promise<number[]> {
  return new Promise((resolve) => {
    const results: number[] = [];
    let prev = now();
    let count = 0;
    function frame() {
      const t = now();
      results.push(t - prev);
      prev = t;
      count++;
      if (count < samples) requestAnimationFrame(frame);
      else resolve(results);
    }
    requestAnimationFrame(frame);
  });
}

async function measureCrossTimerDrift(samples: number): Promise<{
  perfNow: number[];
  dateNow: number[];
  drift: number[];
}> {
  const perfNow: number[] = [];
  const dateNow: number[] = [];
  const drift: number[] = [];

  for (let i = 0; i < samples; i++) {
    const t0 = now();
    const d0 = Date.now();
    await new Promise<void>((r) => requestAnimationFrame(() => r()));
    const t1 = now();
    const d1 = Date.now();
    const perfDelta = t1 - t0;
    const dateDelta = d1 - d0;
    perfNow.push(perfDelta);
    dateNow.push(dateDelta);
    drift.push(perfDelta - dateDelta);
  }

  return { perfNow, dateNow, drift };
}

export async function runTimerTests(config: LabConfig): Promise<TestResult[]> {
  const t0 = now();
  const tests: TestResult[] = [];
  const isProd = config.mode === 'prod';

  // If the tab is throttled in the background, timer measurements become useless.
  const visibility = config.visibilityTracker?.getState() ?? {
    hiddenAtStart: false,
    hiddenDuringRun: false,
    transitions: 0,
    hiddenTimeMs: 0,
    focusedAtStart: true,
    focusedDuringRun: true,
  };

  const counts = {
    perfNow: isProd ? 50 : 100,
    setTimeout: isProd ? 20 : 50,
    raf: isProd ? 20 : 30,
    drift: isProd ? 10 : 15,
  };

  let wasm: Awaited<ReturnType<typeof loadWasmCpu>> | null = null;
  try {
    wasm = await loadWasmCpu('/cpu.wasm');
  } catch {
    // perfnow_jitter will fail below if WASM is unavailable.
  }

  // Calibrate busyLoop so each call takes roughly 0.5-2 ms.
  let busyLoopIterations = 50000;
  if (wasm) {
    const calStart = now();
    wasm.busyLoop(50000);
    const calDuration = now() - calStart;
    if (calDuration > 0) {
      busyLoopIterations = Math.max(10000, Math.round(50000 * 1.0 / calDuration));
    }
  }

  try {
    const samples = await measurePerfNowJitter(counts.perfNow, busyLoopIterations, wasm);
    tests.push({
      name: 'perfnow_jitter',
      group: GROUP,
      success: true,
      durationMs: samples.reduce((a, b) => a + b, 0),
      samples,
      stats: computeStats(samples),
      metadata: {
        ...jitterProfile(samples),
        visibility,
      },
    });
  } catch (err) {
    tests.push({
      name: 'perfnow_jitter',
      group: GROUP,
      success: false,
      durationMs: now() - t0,
      error: err instanceof Error ? err.message : String(err),
    });
  }

  try {
    const samples = await measureSetTimeoutJitter(counts.setTimeout);
    tests.push({
      name: 'settimeout_jitter',
      group: GROUP,
      success: true,
      durationMs: samples.reduce((a, b) => a + b, 0),
      samples,
      stats: computeStats(samples),
      metadata: {
        ...jitterProfile(samples),
        visibility,
      },
    });
  } catch (err) {
    tests.push({
      name: 'settimeout_jitter',
      group: GROUP,
      success: false,
      durationMs: now() - t0,
      error: err instanceof Error ? err.message : String(err),
    });
  }

  try {
    const samples = await measureRafStability(counts.raf);
    tests.push({
      name: 'raf_stability',
      group: GROUP,
      success: true,
      durationMs: samples.reduce((a, b) => a + b, 0),
      samples,
      stats: computeStats(samples),
      metadata: {
        ...jitterProfile(samples),
        visibility,
      },
    });
  } catch (err) {
    tests.push({
      name: 'raf_stability',
      group: GROUP,
      success: false,
      durationMs: now() - t0,
      error: err instanceof Error ? err.message : String(err),
    });
  }

    try {
    const drift = await measureCrossTimerDrift(counts.drift);
    tests.push({
      name: 'cross_timer_drift',
      group: GROUP,
      success: true,
      durationMs: drift.perfNow.reduce((a, b) => a + b, 0),
      metadata: {
        perfNow: computeStats(drift.perfNow),
        dateNow: computeStats(drift.dateNow),
        drift: computeStats(drift.drift),
        visibility,
      },
    });
  } catch (err) {
    tests.push({
      name: 'cross_timer_drift',
      group: GROUP,
      success: false,
      durationMs: now() - t0,
      error: err instanceof Error ? err.message : String(err),
    });
  }

  return tests;
}
